/**
 * V5 — Classement des améliorations par objectif de rénovation.
 *
 * Le frontend récupère les métriques de chaque amélioration applicable via
 * l'endpoint `/applicable-improvements` (ges, heating, label_after, roi_years,
 * summer_score_delta). Ce module fait le tri côté client selon l'objectif
 * choisi, en évitant de relancer une requête au backend.
 *
 * Pour l'objectif "location", l'algorithme combine deux critères :
 *   - atteinte de l'étiquette E (objectif passoires énergétiques 2028)
 *   - gain ordinal d'étiquette (plus le saut est grand, mieux c'est)
 */

import type {
  ImprovementApplicability,
  RenovationObjective,
} from "@/api/scenarios";
import type { ImprovementOption } from "@/api/catalogs";

/** Ordre des étiquettes DPE (A=meilleur, G=pire). */
export const LABEL_ORDER = ["A", "B", "C", "D", "E", "F", "G"] as const;

/** Index d'une étiquette dans LABEL_ORDER. 0 = meilleur, 6 = pire. */
function labelIndex(label: string | undefined): number {
  if (!label) return 6;
  const idx = LABEL_ORDER.indexOf(label as (typeof LABEL_ORDER)[number]);
  return idx < 0 ? 6 : idx;
}

/**
 * Calcule le score de classement d'une amélioration selon l'objectif.
 * Plus le score est élevé, plus l'amélioration est adaptée à l'objectif.
 * Renvoie -Infinity si non applicable / non pertinent.
 */
export function scoreForObjective(
  item: ImprovementApplicability,
  objective: RenovationObjective,
): number {
  if (!item.applicable) return -Infinity;

  switch (objective) {
    case "roi": {
      // ROI individuel (années) : plus c'est petit, mieux c'est. On inverse
      // pour avoir un score ascendant (score élevé = bon).
      const r = item.roi_years;
      if (r == null || r <= 0) return -Infinity;
      return 1000 - r;
    }
    case "carbon":
      return item.ges_per_m2_delta ?? 0;
    case "winter_comfort":
      return item.heating_kwh_ef_delta ?? 0;
    case "summer_comfort":
      return item.summer_score_delta ?? 0;
    case "rental":
      // Cas non couvert ici : l'objectif rental nécessite baseLabel.
      // Utiliser `scoreForObjectiveWithBase` à la place.
      return 0;
  }
}

/**
 * Variante avec accès au `baseLabel` global (pour l'objectif rental).
 * Privilégiée par `rankByObjective`.
 */
export function scoreForObjectiveWithBase(
  item: ImprovementApplicability,
  objective: RenovationObjective,
  baseLabel: string | undefined,
): number {
  if (!item.applicable) return -Infinity;

  if (objective === "rental") {
    const idxAfter = labelIndex(item.label_after);
    const idxBase = labelIndex(baseLabel);
    const idxTarget = labelIndex("E");
    const reaches = idxAfter <= idxTarget;
    const baseScore = reaches ? 1000 : 0;
    const ordinalGain = idxBase - idxAfter;
    return baseScore + ordinalGain * 10 + (item.kwh_saved ?? 0) * 0.001;
  }
  return scoreForObjective(item, objective);
}

/**
 * Classe les améliorations applicables selon l'objectif.
 * Renvoie la liste des clés triées de la meilleure à la moins bonne.
 */
export function rankByObjective(
  items: Record<string, ImprovementApplicability>,
  objective: RenovationObjective,
  baseLabel?: string,
): string[] {
  const entries = Object.entries(items);
  const scored = entries
    .map(([key, item]) => ({
      key,
      score: scoreForObjectiveWithBase(item, objective, baseLabel),
    }))
    .filter((x) => x.score > -Infinity);
  scored.sort((a, b) => b.score - a.score);
  return scored.map((x) => x.key);
}

/**
 * Construit le set suggéré : top-N améliorations par objectif,
 * dédupliqué par applicateur exclusif (un seul geste par applicateur).
 *
 * Exemple : `replace_heating` ne peut être occupé que par UN seul
 * système de chauffage dans le set (PAC OU chaudière OU poêle).
 *
 * Renvoie un objet { selected, excluded } où :
 *   - selected : clés retenues (dans l'ordre de classement)
 *   - excluded : clés écartées en raison d'un conflit avec un mieux classé
 */
export function buildSuggestedSet(
  ranked: string[],
  improvements: ImprovementOption[],
  exclusiveApplicators: string[] | undefined,
  n: number,
): { selected: string[]; excluded: Record<string, string> } {
  const result: string[] = [];
  const excluded: Record<string, string> = {};
  const usedApplicators = new Set<string>();

  for (const key of ranked) {
    if (result.length >= n) break;
    const meta = improvements.find((i) => i.key === key);
    const applicator = meta?.applicator;
    if (
      applicator &&
      exclusiveApplicators?.includes(applicator) &&
      usedApplicators.has(applicator)
    ) {
      // Conflit : une amélioration mieux classée occupe déjà cet applicateur.
      const winner = result.find((k) => {
        const m = improvements.find((i) => i.key === k);
        return m?.applicator === applicator;
      });
      excluded[key] = `conflict_with:${winner ?? "?"}`;
      continue;
    }
    result.push(key);
    if (applicator && exclusiveApplicators?.includes(applicator)) {
      usedApplicators.add(applicator);
    }
  }
  return { selected: result, excluded };
}
